import React, { useState } from 'react';
import { X, Share2, Copy, Check } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'react-hot-toast';
import { useAuthenticatedFetch } from '../utils/api';

const ShareMeetingModal = ({ isOpen, onClose, meetingId, title }) => {
  const { theme } = useTheme();
  const [shareLink, setShareLink] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const authenticatedFetch = useAuthenticatedFetch();

  const generateShareLink = async () => {
    setIsGenerating(true);
    try {
      const response = await authenticatedFetch(
        `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/meetings/${meetingId}/share-link`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const { shareLink } = await response.json();
        setShareLink(shareLink);
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to generate share link');
      }
    } catch (error) {
      console.error('Error generating share link:', error);
      toast.error('Failed to generate share link');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setIsCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy link');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="rounded-lg p-6 w-full max-w-md" style={{ background: theme.background }}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold" style={{ color: theme.text }}>
            Share Meeting
          </h2>
          <button 
            onClick={onClose} 
            style={{ color: theme.accent }}
            className="hover:opacity-80"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-sm mb-2" style={{ color: theme.text }}>
              Share "{title}" with others
            </p>
          </div>

          {!shareLink ? (
            <button
              onClick={generateShareLink}
              disabled={isGenerating}
              className="w-full py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-all"
              style={{ 
                background: theme.primary,
                color: theme.text,
                opacity: isGenerating ? 0.5 : 1,
                cursor: isGenerating ? 'not-allowed' : 'pointer'
              }}
            >
              <Share2 size={18} />
              {isGenerating ? 'Generating...' : 'Generate Share Link'}
            </button>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  className="w-full px-3 py-2 rounded-lg focus:outline-none"
                  style={{ 
                    background: theme.secondary,
                    color: theme.text,
                    border: `1px solid ${theme.border}`
                  }}
                />
                <button
                  onClick={copyToClipboard}
                  className="p-2 rounded-lg transition-colors"
                  style={{ 
                    background: theme.primary,
                    color: theme.text
                  }}
                  title={isCopied ? 'Copied!' : 'Copy to clipboard'}
                >
                  {isCopied ? <Check size={18} /> : <Copy size={18} />}
                </button>
              </div>
              <p className="text-xs" style={{ color: theme.accent }}>
                Anyone with a Classify account can use this link to view and add this meeting to their account.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareMeetingModal; 